// R116 #6 (Madel: "would it be possible to automatically sync the status
// between Issues and Maintenance? … in Issues, we can't mark an item as
// resolved if it has a WO attached"). The Issues board's work-order pieces, in
// the Calendar stay-panel look (soft card, small uppercase section labels with
// count chips, ONE blue full-width primary, pills, muted helper text):
//   woChip(row)          the status chip of a row that follows its WO
//                        ("🛠 WO open · 1/3", "vendor says done", "✓ WO done",
//                        "WO deleted") — words mirror lib/woIssueLink chipLabel
//   askWoClose(...)      the close sheet: optional "what was done" + optional
//                        done-on date (default today) for one row or a batch
//   askWoConfirm(...)    "Close the work order anyway?" (other open work / a
//                        close hazard) → 'force' | 'tick' | null
//   askBulkWoConfirm(..) the same question for a bulk batch → 'force' | null
//   askDismissCloses(..) Q4: "This closes the work order too" + the reason
// Every overlay carries .iss-modal-wrap so the board's 15-minute refresh is
// vetoed while one is open (IssuesPage.suspendAutoRefresh).

const esc = (s: any) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const localToday = () => { try { return new Date().toLocaleDateString('en-CA'); } catch { return new Date().toISOString().slice(0, 10); } };

export type WoInfo = { id: string; chip: string; status?: string | null; done?: number; total?: number; own_key?: string | null; title?: string; assigned_to?: string; after_n?: number };
export type WoConfirm = { wo_id?: string; wo_title?: string; open_items?: number; other_issues?: number; can_tick?: boolean; hazards?: { code: string; why: string }[]; why?: string; board_id?: string };

// ── row helpers ──────────────────────────────────────────────────────────────
// A row whose resolve goes THROUGH an open work order (a wo: row, or an issue
// handed to a WO that is still open).
export function hasOpenWo(r: any): boolean {
  if (!r) return false;
  if (r.source === 'maintenance') return r.status !== 'resolved' && r.status !== 'dismissed';
  const w = r.wo;
  return !!(w && w.id && !['done', 'missing', 'unknown'].includes(String(w.chip)) && w.status !== 'done');
}
export function woIdOf(r: any): string {
  if (!r) return '';
  if (r.source === 'maintenance') return String(r.source_id || '');
  return String((r.wo && r.wo.id) || r.wo_id || '');
}
export function woChipLabel(w: WoInfo | null | undefined): string {
  if (!w) return '';
  const n = w.total ? ` · ${w.done || 0}/${w.total}` : '';
  switch (w.chip) {
    case 'done': return '✓ WO done';
    case 'item_done': return `✓ done on the WO${n}`;
    case 'vendor_done': return `🛠 vendor says done${n}`;
    case 'followup': return '🛠 WO follow-up';
    case 'in_progress': return `🛠 WO in progress${n}`;
    case 'open': return `🛠 WO open${n}`;
    case 'missing': return 'WO deleted';
    default: return '🛠 WO';
  }
}
const CHIP_TONE: Record<string, string> = { done: 'ok', item_done: 'ok', vendor_done: 'info', followup: 'warn', in_progress: 'warn', open: 'warn', missing: 'muted', unknown: 'muted' };
const CHIP_TITLE: Record<string, string> = {
  done: 'The work order is done — this issue is resolved with it. Open it in Maintenance.',
  item_done: "This issue's line on the work order is ticked — resolved, while the WO stays open for its other items. Open it in Maintenance.",
  vendor_done: 'The vendor says it is done — it stays in progress until the office checks and closes the work order.',
  followup: 'The tech flagged more work / a follow-up — the work order stays open, so this issue does too.',
  in_progress: 'This issue is on a work order that is in progress — it resolves when the work order is closed.',
  open: 'This issue is on an open work order — it resolves when the work order is closed (or its line is ticked).',
  missing: 'The work order this issue went to was deleted in Maintenance. Re-assign it if it still needs doing.',
  unknown: "Couldn't read the work orders just now — showing the last known state.",
};
// The status-column chip (a button into Maintenance when the WO exists).
export function woChip(r: any): string {
  const w: WoInfo | null = r && r.wo;
  if (!w || !w.chip) return '';
  const tone = CHIP_TONE[w.chip] || 'muted';
  const title = `${CHIP_TITLE[w.chip] || ''}${w.title ? ` — “${String(w.title).slice(0, 80)}”` : ''}${w.assigned_to ? ` · ${w.assigned_to}` : ''}`;
  if (w.chip === 'missing' || w.chip === 'unknown') return ` <span class="iws-chip tone-${tone}" title="${esc(title)}">${esc(woChipLabel(w))}</span>`;
  return ` <button type="button" class="iws-chip tone-${tone}" data-act="wo-open" data-wo="${esc(w.id)}" title="${esc(title)}">${esc(woChipLabel(w))}</button>`;
}

// ── the overlay shell ───────────────────────────────────────────────────────
export function sheet(html: string, labelledBy = 'iws-title'): { wrap: HTMLElement; close: () => void } {
  ensureCss();
  const wrap = document.createElement('div');
  wrap.className = 'iss-modal-wrap iws-ovl';
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-modal', 'true');
  wrap.setAttribute('aria-labelledby', labelledBy);
  wrap.innerHTML = `<div class="iws-sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = () => { wrap.remove(); document.removeEventListener('keydown', onKey); };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') (wrap.querySelector('[data-iws-cancel]') as HTMLElement | null)?.click(); };
  document.addEventListener('keydown', onKey);
  return { wrap, close };
}
export const head = (icon: string, title: string, sub: string) => `
  <div class="iws-head">
    <span class="iws-ic" aria-hidden="true">${icon}</span>
    <div class="iws-ht"><h3 id="iws-title">${esc(title)}</h3>${sub ? `<div class="iws-sub">${sub}</div>` : ''}</div>
    <button type="button" class="iws-x" data-iws-cancel aria-label="Close">✕</button>
  </div>`;
export const sec = (label: string, count?: number) => `<div class="iws-sec">${esc(label)}${count != null ? ` <span class="iws-cnt">${count}</span>` : ''}</div>`;
export const pill = (txt: string, tone = '') => `<span class="iws-pill${tone ? ` tone-${tone}` : ''}">${esc(txt)}</span>`;

// ── the close sheet (one row or a batch) ────────────────────────────────────
// wos: the work orders this closes (title + property) — a batch lists them.
export function askWoClose(opts: { wos: { title: string; listing_name?: string }[]; verb?: string; issueTitle?: string; mayClose?: boolean }): Promise<{ note: string; done_on: string } | null> {
  return new Promise(resolve => {
    const n = opts.wos.length;
    const one = n === 1 ? opts.wos[0] : null;
    const today = localToday();
    const title = opts.mayClose ? 'Resolve on the work order' : (one ? 'Close the work order' : `Close ${n} work orders`);
    const sub = one ? `“${esc(String(one.title).slice(0, 70))}”${one.listing_name ? ` · ${esc(one.listing_name)}` : ''}` : 'Each one is marked done in Maintenance';
    const list = one ? '' : `${sec('Work orders', n)}<div class="iws-card iws-list">${opts.wos.slice(0, 6).map(w => `<div class="iws-li"><span class="iws-dot"></span><span class="iws-lt">${esc(String(w.title).slice(0, 70))}</span>${w.listing_name ? `<span class="iws-lp">${esc(w.listing_name)}</span>` : ''}</div>`).join('')}${n > 6 ? `<div class="iws-li iws-more">+${n - 6} more</div>` : ''}</div>`;
    const lead = opts.mayClose
      ? `<div class="iws-note">This ticks the issue's line on its work order and <b>closes the work order</b> when nothing else is open on it — otherwise you'll be asked first.</div>`
      : '';
    const { wrap, close } = sheet(`
      ${head('🛠', title, sub)}
      <div class="iws-body">
        ${lead}
        ${list}
        ${sec('What was done')}
        <textarea class="input iws-ta" data-iws-note rows="3" maxlength="2000" placeholder="Optional — e.g. replaced the switch, tested both lights"></textarea>
        <div class="iws-help">Optional. It becomes the work order's completion report, which the owner sees on the care report — leave it blank if it isn't owner-ready.</div>
        ${sec('Done on')}
        <input type="date" class="input iws-date" data-iws-date value="${today}" max="${today}" aria-label="Done on">
        <div class="iws-help">Defaults to today. Pick the day the work was actually done so the owner report and today's count stay right.</div>
      </div>
      <div class="iws-foot">
        <button type="button" class="btn btn-primary iws-primary" data-iws-go>${esc(opts.verb || (one ? '✓ Close work order' : `✓ Close ${n} work orders`))}</button>
        <button type="button" class="btn btn-ghost iws-secondary" data-iws-cancel>Cancel</button>
      </div>`);
    const done = (v: { note: string; done_on: string } | null) => { close(); resolve(v); };
    wrap.querySelectorAll('[data-iws-cancel]').forEach(el => el.addEventListener('click', () => done(null)));
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(null); });
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => {
      const note = ((wrap.querySelector('[data-iws-note]') as HTMLTextAreaElement).value || '').trim();
      let d = ((wrap.querySelector('[data-iws-date]') as HTMLInputElement).value || '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || d > today) d = today;
      done({ note, done_on: d === today ? '' : d });
    });
    (wrap.querySelector('[data-iws-go]') as HTMLElement | null)?.focus();
  });
}

// ── "close the WO anyway?" (one row) ────────────────────────────────────────
export function askWoConfirm(c: WoConfirm): Promise<'force' | 'tick' | null> {
  return new Promise(resolve => {
    const hz = c.hazards || [];
    const pills = [
      c.open_items ? pill(`${c.open_items} other open item${c.open_items === 1 ? '' : 's'}`, 'warn') : '',
      c.other_issues ? pill(`${c.other_issues} other issue${c.other_issues === 1 ? '' : 's'} on it`, 'warn') : '',
      ...hz.map(h => pill(h.why, 'bad')),
    ].filter(Boolean).join('');
    const what = hz.length && !c.open_items && !c.other_issues
      ? 'Closing it now marks it done in Maintenance even though:'
      : 'Resolving this would close the work order, but it still has other work on it:';
    const { wrap, close } = sheet(`
      ${head('🛠', 'Close the work order anyway?', c.wo_title ? `“${esc(String(c.wo_title).slice(0, 70))}”` : '')}
      <div class="iws-body">
        <div class="iws-card">
          <div class="iws-note">${what}</div>
          <div class="iws-pills">${pills}</div>
        </div>
        <div class="iws-help">${c.can_tick ? "“Just tick this item” marks this issue's line done — the issue reads resolved and the work order stays open for the rest." : 'Closing marks the whole work order done in Maintenance. Cancel to leave it open.'}</div>
      </div>
      <div class="iws-foot">
        <button type="button" class="btn btn-primary iws-primary" data-iws-go>Close the work order anyway</button>
        ${c.can_tick ? '<button type="button" class="btn btn-secondary iws-secondary" data-iws-tick>Just tick this item — WO stays open</button>' : ''}
        <button type="button" class="btn btn-ghost iws-secondary" data-iws-cancel>Cancel</button>
      </div>`);
    const done = (v: 'force' | 'tick' | null) => { close(); resolve(v); };
    wrap.querySelectorAll('[data-iws-cancel]').forEach(el => el.addEventListener('click', () => done(null)));
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(null); });
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => done('force'));
    wrap.querySelector('[data-iws-tick]')?.addEventListener('click', () => done('tick'));
  });
}

// ── the bulk version: N work orders still have other open work ─────────────
export function askBulkWoConfirm(list: (WoConfirm & { title?: string })[]): Promise<'force' | null> {
  return new Promise(resolve => {
    const byWo = new Map<string, WoConfirm & { n: number; title?: string }>();
    for (const c of list) { const k = String(c.wo_id || c.board_id || ''); const e = byWo.get(k); if (e) e.n++; else byWo.set(k, { ...c, n: 1 }); }
    const rows = [...byWo.values()];
    const { wrap, close } = sheet(`
      ${head('🛠', `${rows.length} work order${rows.length === 1 ? '' : 's'} still ha${rows.length === 1 ? 's' : 've'} other work`, 'Close them anyway, or leave them open?')}
      <div class="iws-body">
        ${sec('Work orders', rows.length)}
        <div class="iws-card iws-list">${rows.slice(0, 8).map(c => `<div class="iws-li"><span class="iws-dot tone-warn"></span><span class="iws-lt">${esc(String(c.wo_title || c.wo_id || '').slice(0, 60))}</span><span class="iws-lp">${esc(c.why || '')}</span></div>`).join('')}${rows.length > 8 ? `<div class="iws-li iws-more">+${rows.length - 8} more</div>` : ''}</div>
        <div class="iws-help">Leaving them open keeps those issues in progress on their work orders. A work order the owner hasn't approved, one booked for a later day, or one with a timer running is never closed in bulk — close those one at a time.</div>
      </div>
      <div class="iws-foot">
        <button type="button" class="btn btn-primary iws-primary" data-iws-go>Close ${rows.length === 1 ? 'it' : 'them'} anyway</button>
        <button type="button" class="btn btn-ghost iws-secondary" data-iws-cancel>Leave ${rows.length === 1 ? 'it' : 'them'} open</button>
      </div>`);
    const done = (v: 'force' | null) => { close(); resolve(v); };
    wrap.querySelectorAll('[data-iws-cancel]').forEach(el => el.addEventListener('click', () => done(null)));
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(null); });
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => done('force'));
  });
}

// ── Q4: dismissing an open work order's row closes the work order too ───────
export function askDismissCloses(opts: { n: number; titles: string[]; alsoPlain?: number }): Promise<string | null> {
  return new Promise(resolve => {
    const { n } = opts;
    const { wrap, close } = sheet(`
      ${head('🛠', n === 1 ? 'This closes the work order too' : `This closes ${n} work orders too`, n === 1 ? `“${esc(String(opts.titles[0] || '').slice(0, 70))}”` : 'Dismissing a work order row marks the work order done in Maintenance')}
      <div class="iws-body">
        ${n > 1 ? `${sec('Work orders', n)}<div class="iws-card iws-list">${opts.titles.slice(0, 6).map(t => `<div class="iws-li"><span class="iws-dot"></span><span class="iws-lt">${esc(String(t).slice(0, 70))}</span></div>`).join('')}${n > 6 ? `<div class="iws-li iws-more">+${n - 6} more</div>` : ''}</div>` : ''}
        ${opts.alsoPlain ? `<div class="iws-note">${opts.alsoPlain} other issue${opts.alsoPlain === 1 ? '' : 's'} in the selection ${opts.alsoPlain === 1 ? 'is' : 'are'} just dismissed.</div>` : ''}
        ${sec('Why dismiss it?')}
        <textarea class="input iws-ta" data-iws-reason rows="2" maxlength="300" placeholder="Optional — e.g. duplicate, already handled by the owner"></textarea>
        <div class="iws-help">The reason is written on the work order's log, so Maintenance sees why it closed. Restore undoes it.</div>
      </div>
      <div class="iws-foot">
        <button type="button" class="btn btn-primary iws-primary" data-iws-go>Dismiss &amp; close ${n === 1 ? 'the work order' : `${n} work orders`}</button>
        <button type="button" class="btn btn-ghost iws-secondary" data-iws-cancel>Cancel</button>
      </div>`);
    const done = (v: string | null) => { close(); resolve(v); };
    wrap.querySelectorAll('[data-iws-cancel]').forEach(el => el.addEventListener('click', () => done(null)));
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(null); });
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => done(((wrap.querySelector('[data-iws-reason]') as HTMLTextAreaElement).value || '').trim()));
  });
}

// ── styles (one prefixed block, injected once; tokens only) ─────────────────
let cssDone = false;
export function ensureCss(): void {
  if (cssDone || typeof document === 'undefined') return;
  if (document.getElementById('iws-css')) { cssDone = true; return; }
  const st = document.createElement('style');
  st.id = 'iws-css';
  st.textContent = WO_SYNC_CSS;
  document.head.appendChild(st);
  cssDone = true;
}
export const WO_SYNC_CSS = `
  .iss-modal-wrap.iws-ovl { position:fixed; inset:0; z-index:9600; background:rgba(15,23,42,.36); display:flex; align-items:center; justify-content:center; padding:16px; box-sizing:border-box; overflow:hidden; }
  .iws-sheet { width:460px; max-width:100%; max-height:calc(100vh - 32px); display:flex; flex-direction:column; background:var(--card); border:1px solid var(--border); border-radius:var(--radius-lg); box-shadow:var(--shadow-pop); overflow:hidden; font-size:13px; color:var(--text); }
  .iws-head { display:flex; align-items:flex-start; gap:12px; padding:16px 18px 12px; border-bottom:1px solid var(--border-soft); }
  .iws-ic { flex:none; width:40px; height:40px; border-radius:var(--radius-sm); background:var(--accent-soft); color:var(--accent); display:inline-flex; align-items:center; justify-content:center; font-size:18px; }
  .iws-ht { flex:1; min-width:0; }
  .iws-ht h3 { margin:0; font-size:15px; font-weight:700; letter-spacing:-0.01em; color:var(--text); }
  .iws-sub { font-size:12px; color:var(--text-muted); margin-top:3px; overflow-wrap:anywhere; }
  .iws-x { flex:none; width:36px; height:36px; border-radius:var(--radius-pill); border:1px solid transparent; background:transparent; color:var(--text-muted); font-size:14px; cursor:pointer; }
  .iws-x:hover { background:var(--bg-subtle); border-color:var(--border); color:var(--text); }
  .iws-body { flex:1; overflow-y:auto; padding:6px 18px 4px; }
  .iws-sec { display:flex; align-items:center; gap:6px; margin:14px 0 6px; font-size:10.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--text-muted); }
  .iws-cnt { display:inline-flex; align-items:center; justify-content:center; min-width:18px; height:18px; padding:0 6px; border-radius:var(--radius-pill); background:var(--bg-subtle); border:1px solid var(--border); color:var(--text-muted); font-size:10.5px; letter-spacing:0; }
  .iws-card { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); padding:10px 12px; margin-top:8px; }
  .iws-list { padding:4px 0; }
  .iws-li { display:flex; align-items:center; gap:8px; padding:7px 12px; font-size:12.5px; min-width:0; }
  .iws-li + .iws-li { border-top:1px solid var(--border-soft); }
  .iws-dot { flex:none; width:8px; height:8px; border-radius:var(--radius-pill); background:var(--accent); }
  .iws-dot.tone-warn { background:var(--warning); }
  .iws-lt { flex:1; min-width:0; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .iws-lp { flex:none; max-width:50%; color:var(--text-muted); font-size:11.5px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .iws-more { color:var(--text-muted); font-weight:600; }
  .iws-note { font-size:12.5px; color:var(--text); line-height:1.45; margin-top:8px; }
  .iws-help { font-size:11.5px; color:var(--text-muted); line-height:1.45; margin-top:6px; }
  .iws-pills { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
  .iws-pill { display:inline-flex; align-items:center; min-height:22px; padding:0 9px; border-radius:var(--radius-pill); font-size:11.5px; font-weight:600; border:1px solid var(--border); background:var(--bg-subtle); color:var(--text-muted); }
  .iws-pill.tone-warn { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
  .iws-pill.tone-bad { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger-border); }
  .iws-ta { width:100%; box-sizing:border-box; resize:vertical; min-height:64px; font-size:13px; font-family:inherit; }
  .iws-date { width:auto; min-width:170px; min-height:38px; font-size:13px; }
  .iws-foot { display:flex; flex-direction:column; gap:8px; padding:12px 18px 16px; border-top:1px solid var(--border-soft); background:var(--card); }
  .iws-foot .btn { width:100%; justify-content:center; min-height:42px; font-size:13px; }
  .iws-foot .iws-secondary { font-weight:600; }
  /* status-column chips on the board (buttons into Maintenance) */
  .iws-chip { display:inline-flex; align-items:center; gap:4px; min-height:22px; padding:0 9px; border-radius:var(--radius-pill); font:inherit; font-size:11px; font-weight:700; line-height:1.2; border:1px solid var(--border); background:var(--bg-subtle); color:var(--text-muted); white-space:nowrap; }
  button.iws-chip { cursor:pointer; }
  button.iws-chip:hover { border-color:var(--accent-border); }
  .iws-chip.tone-warn { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
  .iws-chip.tone-ok { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border); }
  .iws-chip.tone-info { background:var(--accent-soft); color:var(--accent); border-color:var(--accent-border); }
  @media (max-width: 720px) {
    .iss-modal-wrap.iws-ovl { align-items:flex-end; padding:0; }
    .iws-sheet { width:100%; max-height:92vh; border-radius:var(--radius-lg) var(--radius-lg) 0 0; border-bottom:none; }
    .iws-foot { position:sticky; bottom:0; padding-bottom:calc(16px + env(safe-area-inset-bottom, 0px)); }
    .iws-foot .btn { min-height:44px; }
    .iws-ta, .iws-date { font-size:16px; }
    .iws-chip { min-height:36px; padding:0 12px; font-size:12px; }
  }
`;
